import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { useUpdateGameV2 } from "@seabrookstudios/pitch2table";
import { formatPlayerCount } from "./formatters";
import { FullScreenDialog } from "../core/FullScreenDialog";
import Box from "@mui/material/Box";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import ButtonGroup from "@mui/material/ButtonGroup";
import CardHeader from "@mui/material/CardHeader";

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").Game} props.game
 */
export const EditPlayersControl = ({ game }) => {
  const onUpdateSuccess = () => {
    enqueueSnackbar("Saved", { variant: "success" });
  };

  const onUpdateFailure = () => {
    enqueueSnackbar("Update failed", { variant: "error" });
  };

  const updateGame = useUpdateGameV2(game.id, onUpdateSuccess, onUpdateFailure);

  /**
   * @param {string} playerCount
   * @param {number} minPlayerCount
   * @param {number} maxPlayerCount
   * @param {import('@seabrookstudios/pitch2table-core').SupportPlayerCounts} playerCounts
   */
  const onSave = async (playerCount, minPlayerCount, maxPlayerCount, playerCounts) => {
    await updateGame.mutateAsync({
      playerCount,
      minPlayerCount,
      maxPlayerCount,
      playerCounts,
      sizzle: undefined,
      bgg: undefined,
      personalGoal: undefined,
    });
  };

  const [newPlayerCount, setPlayerCount] = useState(game.playerCount || "");
  const [newMin, setMin] = useState(`${game.minPlayerCounts}`);
  const [newMax, setMax] = useState(`${game.maxPlayerCounts}`);
  const [newType, setType] = useState(game.playerCounts);
  const [disabled, setDisabled] = useState(true);

  /**
   * Save the value with some behaviour. All of this is tricky as parseInt will never throw
   * - swap min and max if that makes more sense
   * - switch to single if min and max are the same value
   * - other values are accepted as is
   */
  const onSaveHandler = () => {
    if (newType === "other") {
      onSave(newPlayerCount, newMin, newMax, "other");
      return;
    }

    const nMin = Number.parseInt(newMin, 10);
    const nMax = Number.parseInt(newMax, 10);

    const finalMin = nMin < nMax ? nMin : nMax;
    const finalMax = nMin > nMax ? nMin : nMax;

    const finalType = finalMin === finalMax ? "single" : newType;
    const finalPlayerCount = finalType === "single" ? `${finalMin}` : newPlayerCount;

    onSave(finalPlayerCount, finalMin, finalMax, finalType);
  };

  const checkDisabledState = () => {
    if (newType === "other") {
      setDisabled(newPlayerCount.length === 0);
    }
    if (newType === "single") {
      const value = Number.parseInt(newMin, 10);
      setDisabled(Number.isNaN(value) || newMin.length === 0);
    }
    if (newType === "range") {
      const v1 = Number.parseInt(newMin, 10);
      const v2 = Number.parseInt(newMax, 10);
      setDisabled(Number.isNaN(v1) || newMin.length === 0 || Number.isNaN(v2) || newMax.length === 0);
    }
  };

  useEffect(checkDisabledState, [newPlayerCount, newMax, newMin, newType]);

  /**
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  const setOtherFromEvent = (e) => {
    const v = e.target.value;

    setOther(v);
  };

  /**
   * @param {string} v
   */
  const setOther = (v) => {
    setPlayerCount(v);
    setMin("");
    setMax("");
  };

  /**
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  const setSingleFromEvent = (e) => {
    const v = e.target.value;

    setSingle(v);
  };

  /**
   * @param {string} v
   */
  const setSingle = (v) => {
    if (v.length === 0) {
      setPlayerCount("");
      setMin("");
      setMax("");
      return;
    }

    const value = Number.parseInt(v, 10);
    if (Number.isNaN(value)) {
      setMin("");
      setMax("");
      setPlayerCount("");
      return;
    }

    setPlayerCount(v);
    setMin(`${value}`);
    setMax(`${value}`);
  };

  /**
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  const setRangeLowerFromEvent = (e) => {
    const v = e.target.value;

    setRangeLower(v);
  };

  /**
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  const setRangeUpperFromEvent = (e) => {
    const v = e.target.value;

    setRangeUpper(v);
  };

  /**
   * @param {string} v
   */
  const setRangeLower = (v) => {
    if (v.length === 0) {
      setPlayerCount("");
      setMin("");
      return;
    }

    const value = Number.parseInt(v, 10);
    if (Number.isNaN(value)) {
      setPlayerCount("");
      setMin("");
      return;
    }

    setPlayerCount(`${value}-${newMax}`);
    setMin(`${value}`);
  };

  /**
   * @param {string} v
   */
  const setRangeUpper = (v) => {
    if (v.length === 0) {
      setPlayerCount("");
      setMax("");
      return;
    }

    const value = Number.parseInt(v, 10);
    if (Number.isNaN(value)) {
      setPlayerCount("");
      setMax("");
      return;
    }

    setPlayerCount(`${newMin}-${value}`);
    setMax(`${value}`);
  };

  /**
   * @param {number} n
   */
  const setQuick = (n) => {
    const onPressHandler = () => {
      setType("single");
      setSingle(`${n}`);
    };

    return onPressHandler;
  };

  /**
   * @param {number} n1
   * @param {number} n2
   */
  const setQuickRange = (n1, n2) => {
    const onPressHandler = () => {
      setType("range");
      setMin(`${n1}`);
      setMax(`${n2}`);
      setPlayerCount(`${n1}-${n2}`);
    };

    return onPressHandler;
  };

  return (
    <FullScreenDialog
      title={`Edit players`}
      actionText="SAVE"
      onSaveHandler={onSaveHandler}
      isBusy={updateGame.isPending}
      CustomControl={
        <ListItemText primary={"Players"} secondary={formatPlayerCount(game.playerCount) || "Click to edit."} />
      }
      canSave={disabled}
    >
      <Box padding={2} display="flex" justifyContent="center">
        <ButtonGroup variant="outlined" disableElevation>
          <Button
            variant={newType === "single" ? "contained" : "outlined"}
            style={{ width: "100%" }}
            onClick={() => setType("single")}
          >
            SINGLE
          </Button>
          <Button
            variant={newType === "range" ? "contained" : "outlined"}
            style={{ width: "100%" }}
            onClick={() => setType("range")}
          >
            RANGE
          </Button>
          <Button
            variant={newType === "other" ? "contained" : "outlined"}
            style={{ width: "100%" }}
            onClick={() => setType("other")}
          >
            OTHER
          </Button>
        </ButtonGroup>
      </Box>
      <Box p={1}>
        {newType === "other" && (
          <Stack direction="row">
            <TextField
              style={{ width: "100%" }}
              value={newPlayerCount}
              label="Non-Standard Player Count"
              autoFocus
              onChange={setOtherFromEvent}
              helperText="How many players does this game support e.g. '2, 4'"
            />
          </Stack>
        )}
        {newType === "single" && (
          <Stack direction="row">
            <TextField
              style={{ width: "100%" }}
              value={newMin}
              label="Exact Player Count"
              autoFocus
              onChange={setSingleFromEvent}
              type="number"
              helperText="How many players does this game support e.g. 2"
            />
          </Stack>
        )}
        {newType === "range" && (
          <Stack direction="row">
            <TextField
              value={newMin}
              style={{ width: "100%" }}
              label="Minimum player count"
              autoFocus
              onChange={setRangeLowerFromEvent}
              type="number"
              helperText="How many players does this game support"
            />
            <Box paddingLeft={1} />
            <TextField
              value={newMax}
              style={{ width: "100%" }}
              label="Maximum player count"
              autoFocus
              onChange={setRangeUpperFromEvent}
              type="number"
              helperText="e.g. 1-2, 1-5, 3-4, etc"
            />
          </Stack>
        )}
      </Box>
      <Box padding={1}>
        <CardHeader subheader="Quick Options" />
        <Grid container spacing={2}>
          <Grid item size={{ xs: 4 }}>
            <Button style={{ width: "100%" }} variant="outlined" onClick={setQuick(1)}>
              Solo
            </Button>
          </Grid>
          <Grid item size={{ xs: 4 }}>
            <Button style={{ width: "100%" }} variant="outlined" onClick={setQuick(2)}>
              2p
            </Button>
          </Grid>
          <Grid item size={{ xs: 4 }}>
            <Button style={{ width: "100%" }} variant="outlined" onClick={setQuickRange(1, 4)}>
              1 - 4
            </Button>
          </Grid>
          <Grid item size={{ xs: 4 }}>
            <Button style={{ width: "100%" }} variant="outlined" onClick={setQuickRange(1, 5)}>
              1 - 5
            </Button>
          </Grid>
          <Grid item size={{ xs: 4 }}>
            <Button style={{ width: "100%" }} variant="outlined" onClick={setQuickRange(2, 4)}>
              2 - 4
            </Button>
          </Grid>
          <Grid item size={{ xs: 4 }}>
            <Button style={{ width: "100%" }} variant="outlined" onClick={setQuickRange(2, 5)}>
              2 - 5
            </Button>
          </Grid>
          <Grid item size={{ xs: 4 }}>
            <Button style={{ width: "100%" }} variant="outlined" onClick={setQuickRange(2, 6)}>
              2 - 6
            </Button>
          </Grid>
          <Grid item size={{ xs: 4 }}>
            <Button style={{ width: "100%" }} variant="outlined" onClick={setQuickRange(3, 6)}>
              3 - 6
            </Button>
          </Grid>
          <Grid item size={{ xs: 4 }}>
            <Button style={{ width: "100%" }} variant="outlined" onClick={setQuick(99)}>
              99
            </Button>
          </Grid>
        </Grid>
      </Box>
    </FullScreenDialog>
  );
};
